import React, { useState, useEffect } from 'react';
import { Users, Mail, Trash2, Loader2, Plus, Crown, Shield } from 'lucide-react';
import { api } from '../../api';
import { useAuth } from '../Auth/AuthContext';

export default function TeamPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ users: [], pending_invitations: [] });
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => { loadTeam(); }, []);

  const loadTeam = async () => {
    setLoading(true);
    try {
      const d = await api.getUsers();
      setData(d);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true); setError(''); setSuccess('');
    try {
      await api.inviteUser(email);
      setSuccess(`Inbjudan skickad till ${email}`);
      setEmail('');
      loadTeam();
    } catch (e) { setError(e.message); }
    finally { setInviting(false); }
  };

  const handleRemove = async (id, userEmail) => {
    if (!window.confirm(`Ta bort ${userEmail} från teamet?`)) return;
    try {
      await api.removeUser(id);
      loadTeam();
    } catch (e) { setError(e.message); }
  };

  const handleCancelInvite = async (inviteEmail) => {
    try {
      await api.cancelInvitation(inviteEmail);
      loadTeam();
    } catch (e) { setError(e.message); }
  };

  const handleToggleRole = async (id, currentRole) => {
    const newRole = currentRole === 'admin' ? 'member' : 'admin';
    if (!window.confirm(`Ändra rollen till ${newRole === 'admin' ? 'Admin' : 'Medlem'}?`)) return;
    try {
      await api.updateUserRole(id, newRole);
      loadTeam();
    } catch (e) { setError(e.message); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <h2 className="text-2xl font-black flex items-center gap-3">
        <Users className="text-blue-600" /> Teamhantering
      </h2>

      {/* Bjud in */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-black text-lg mb-4">Bjud in kollega</h3>
        <form onSubmit={handleInvite} className="flex gap-3">
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-3.5 text-slate-400" size={18} />
            <input
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="kollega@foretag.se"
            />
          </div>
          <button type="submit" disabled={inviting}
            className="bg-blue-600 text-white px-5 py-3 rounded-xl font-bold flex items-center gap-2 flex-shrink-0">
            {inviting ? <Loader2 className="animate-spin" size={18} /> : <><Plus size={18} /> Bjud in</>}
          </button>
        </form>
        {error && <p className="text-red-600 text-sm mt-3 bg-red-50 p-3 rounded-lg">{error}</p>}
        {success && <p className="text-green-700 text-sm mt-3 bg-green-50 p-3 rounded-lg">{success}</p>}
      </div>

      {/* Teammedlemmar */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-black text-lg mb-4">Aktiva medlemmar</h3>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="animate-spin text-blue-600" size={24} /></div>
        ) : (
          <div className="space-y-2">
            {data.users.map(u => (
              <div key={u.id} className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black
                    ${u.role === 'admin' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
                    {u.role === 'admin' ? <Crown size={18} /> : (u.email[0].toUpperCase())}
                  </div>
                  <div>
                    <p className="font-bold text-sm">{u.name || u.email}</p>
                    {u.name && <p className="text-xs text-slate-400">{u.email}</p>}
                    <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full
                      ${u.role === 'admin' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>
                      {u.role === 'admin' ? 'Admin' : 'Medlem'}
                    </span>
                  </div>
                </div>
                {u.id !== user.id && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleToggleRole(u.id, u.role)}
                      title={u.role === 'admin' ? 'Degradera till Medlem' : 'Gör till Admin'}
                      className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg text-xs font-bold"
                    >
                      <Shield size={16} />
                    </button>
                    <button
                      onClick={() => handleRemove(u.id, u.email)}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
                {u.id === user.id && <span className="text-xs text-slate-400 font-bold">Du</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Väntande inbjudningar */}
      {data.pending_invitations.length > 0 && (
        <div className="bg-white rounded-2xl border border-amber-200 bg-amber-50 p-6">
          <h3 className="font-black text-lg mb-4 text-amber-800">Väntande inbjudningar</h3>
          <div className="space-y-2">
            {data.pending_invitations.map(inv => (
              <div key={inv.email} className="flex items-center justify-between p-3 bg-white rounded-xl border border-amber-100">
                <div className="flex items-center gap-3">
                  <Mail className="text-amber-500" size={18} />
                  <p className="font-bold text-sm">{inv.email}</p>
                </div>
                <button
                  onClick={() => handleCancelInvite(inv.email)}
                  className="text-slate-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
